import moment from "moment";

/** 统一 API 返回的日期字段为 YYYY-MM-DD */
export function normalizeTrendDate(d) {
  if (d == null || d === "") return "";
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const SERIES_COLORS = [
  { primary: "#1890ff" },
  { primary: "#52c41a" },
  { primary: "#faad14" },
  { primary: "#f5222d" },
  { primary: "#722ed1" },
  { primary: "#13c2c2" },
  { primary: "#eb2f96" },
  { primary: "#fa8c16" },
];

/**
 * 账号维度：同一 account+exchange 下，按 trade_type（spot/swap）拆成多交易对成交量曲线 + 合计
 */
export function buildAccountPairGroups(accountPairRaw) {
  const byAe = new Map();
  (accountPairRaw || []).forEach((row) => {
    const ae = `${row.account}|${row.exchange}`;
    if (!byAe.has(ae)) {
      byAe.set(ae, {
        account: row.account,
        exchange: row.exchange,
        spot: [],
        swap: [],
      });
    }
    const g = byAe.get(ae);
    const tt = String(row.trade_type || "").toLowerCase();
    if (tt === "spot") g.spot.push(row);
    else g.swap.push(row);
  });

  const out = [];
  for (const [, g] of byAe) {
    const spot = buildSidePairLines(g.spot);
    const swap = buildSidePairLines(g.swap);
    const periodVol = (spot.periodVolume || 0) + (swap.periodVolume || 0);
    out.push({
      key: `${g.account}|${g.exchange}`,
      account: g.account,
      exchange: g.exchange,
      spot,
      swap,
      periodVolume: periodVol,
    });
  }
  out.sort((a, b) => b.periodVolume - a.periodVolume);
  return out;
}

function buildSidePairLines(rows) {
  if (!rows.length) {
    return {
      hasData: false,
      chartData: { labels: [], datasets: [] },
      periodVolume: 0,
      periodMakerVolume: 0,
    };
  }
  const pairMap = new Map();
  const dateSet = new Set();
  let periodVolume = 0;
  let periodMakerVolume = 0;

  rows.forEach((r) => {
    const d = normalizeTrendDate(r.date);
    dateSet.add(d);
    periodVolume += parseFloat(r.total_volume || 0);
    periodMakerVolume += parseFloat(r.maker_volume || 0);
    if (!pairMap.has(r.pair)) pairMap.set(r.pair, []);
    pairMap.get(r.pair).push({
      date: d,
      volume: parseFloat(r.total_volume || 0),
      maker_volume: parseFloat(r.maker_volume || 0),
    });
  });

  const sortedDates = [...dateSet].sort();
  const labels = sortedDates.map((d) => moment(d).format("MM-DD"));

  const datasets = [];
  let idx = 0;
  const pairsSorted = [...pairMap.keys()].sort();
  for (const pair of pairsSorted) {
    const points = pairMap.get(pair);
    const byD = new Map(points.map((p) => [p.date, p]));
    const volData = sortedDates.map((d) => (byD.has(d) ? byD.get(d).volume : 0));
    const c = SERIES_COLORS[idx % SERIES_COLORS.length];
    idx += 1;
    datasets.push({
      label: pair,
      data: volData,
      borderColor: c.primary,
      backgroundColor: "transparent",
      tension: 0.25,
      pointRadius: 2,
      yAxisID: "y",
    });
  }

  const totalVol = sortedDates.map((_, i) =>
    datasets.reduce((s, ds) => s + (ds.data[i] || 0), 0),
  );
  datasets.push({
    label: "合计",
    data: totalVol,
    borderColor: "#262626",
    backgroundColor: "transparent",
    borderDash: [6, 4],
    tension: 0.2,
    pointRadius: 2,
    borderWidth: 2,
    yAxisID: "y",
  });

  return {
    hasData: true,
    chartData: { labels, datasets },
    periodVolume,
    periodMakerVolume,
  };
}

/**
 * 交易对维度：每个 pair 下拆成现货 / 合约；各为「成交量 + Maker」双轴小图
 */
export function buildPairSplitGroups(pairRaw) {
  const byPair = new Map();
  (pairRaw || []).forEach((row) => {
    const p = row.pair;
    if (!byPair.has(p)) {
      byPair.set(p, {
        pair: p,
        exchanges: row.exchanges || "",
        spot: [],
        swap: [],
      });
    }
    const g = byPair.get(p);
    const tt = String(row.trade_type || "").toLowerCase();
    if (tt === "spot") g.spot.push(row);
    else g.swap.push(row);
    if (row.exchanges) g.exchanges = row.exchanges;
  });

  const out = [];
  for (const [, g] of byPair) {
    const spot = buildSinglePairTimeSeries(g.spot);
    const swap = buildSinglePairTimeSeries(g.swap);
    const periodVolume = (spot.periodVolume || 0) + (swap.periodVolume || 0);
    out.push({
      key: g.pair,
      pair: g.pair,
      exchanges: g.exchanges,
      spot,
      swap,
      periodVolume,
    });
  }
  out.sort((a, b) => b.periodVolume - a.periodVolume);
  return out;
}

function buildSinglePairTimeSeries(rows) {
  if (!rows.length) {
    return {
      hasData: false,
      chartData: { labels: [], datasets: [] },
      periodVolume: 0,
      periodMakerVolume: 0,
    };
  }
  const dateSet = new Set();
  let periodVolume = 0;
  let periodMakerVolume = 0;
  rows.forEach((r) => {
    dateSet.add(normalizeTrendDate(r.date));
    periodVolume += parseFloat(r.total_volume || 0);
    periodMakerVolume += parseFloat(r.maker_volume || 0);
  });
  const sortedDates = [...dateSet].sort();
  const labels = sortedDates.map((d) => moment(d).format("MM-DD"));
  const byD = new Map(
    rows.map((r) => [normalizeTrendDate(r.date), r]),
  );
  const volData = sortedDates.map((d) =>
    byD.has(d) ? parseFloat(byD.get(d).total_volume || 0) : 0,
  );
  const mkData = sortedDates.map((d) =>
    byD.has(d) ? parseFloat(byD.get(d).maker_volume || 0) : 0,
  );

  return {
    hasData: true,
    chartData: {
      labels,
      datasets: [
        {
          label: "成交量",
          data: volData,
          borderColor: "#1890ff",
          backgroundColor: "rgba(24, 144, 255, 0.08)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          yAxisID: "y",
        },
        {
          label: "Maker量",
          data: mkData,
          borderColor: "#52c41a",
          backgroundColor: "rgba(82, 196, 26, 0.08)",
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          yAxisID: "y1",
        },
      ],
    },
    periodVolume,
    periodMakerVolume,
  };
}

/**
 * 交易所维度：同一 exchange 下拆现货 / 合约，各为成交量+Maker 双轴日趋势
 */
export function buildExchangeVenueVolumeGroups(exchangeDailyRaw) {
  const byEx = new Map();
  (exchangeDailyRaw || []).forEach((row) => {
    const ex = row.exchange || "";
    if (!byEx.has(ex)) {
      byEx.set(ex, { exchange: ex, spot: [], swap: [] });
    }
    const g = byEx.get(ex);
    const tt = String(row.trade_type || "").toLowerCase();
    if (tt === "spot") g.spot.push(row);
    else g.swap.push(row);
  });

  const out = [];
  for (const [, g] of byEx) {
    const spot = buildSinglePairTimeSeries(g.spot);
    const swap = buildSinglePairTimeSeries(g.swap);
    out.push({
      key: g.exchange,
      exchange: g.exchange,
      spot,
      swap,
      periodVolume: (spot.periodVolume || 0) + (swap.periodVolume || 0),
    });
  }
  out.sort((a, b) => b.periodVolume - a.periodVolume);
  return out;
}
